import React from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  Chip
} from "@mui/material";
import {
  Groups as TeamIcon,
  EmojiEvents as AwardIcon,
  LocationOn as LocationIcon,
  TrendingUp as GrowthIcon
} from "@mui/icons-material";

const About = () => {
  const theme = useTheme();

  const stats = [
    { number: "5,000+", label: "Properties Cleaned" },
    { number: "50+", label: "Cities Served" },
    { number: "4.9★", label: "Customer Rating" },
    { number: "98%", label: "Satisfaction Rate" }
  ];

  const values = [
    {
      icon: "✨",
      title: "Excellence",
      description: "We deliver exceptional cleaning quality that exceeds expectations every time."
    },
    {
      icon: "🤝",
      title: "Trust",
      description: "Building long-term relationships based on reliability and transparency."
    },
    {
      icon: "⚡",
      title: "Efficiency",
      description: "Using technology to streamline operations and provide better service."
    },
    {
      icon: "🌱",
      title: "Sustainability",
      description: "Eco-friendly cleaning products and sustainable business practices."
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Hero Section */}
      <Box sx={{ textAlign: "center", mb: 8 }}>
        <Chip 
          label="Our Story" 
          sx={{ 
            mb: 3,
            background: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.dark,
            fontWeight: 600,
            fontSize: "0.85rem"
          }}
        />
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 3,
            fontSize: { xs: "2.5rem", md: "3rem" }
          }}
        >
          About FreshSweeper
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.secondary,
            maxWidth: "800px",
            mx: "auto",
            lineHeight: 1.6,
            fontSize: "1.1rem"
          }}
        >
          Founded in 2020, FreshSweeper began with a simple mission: to make professional 
          cleaning services accessible and reliable for Airbnb hosts and vacation rental 
          property managers. Today, we're the trusted choice for thousands of properties nationwide.
        </Typography>
      </Box>

      {/* Stats */}
      <Grid container spacing={4} sx={{ mb: 8 }}>
        {stats.map((stat, index) => (
          <Grid item xs={6} md={3} key={index}>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h3" fontWeight="700" color={theme.palette.primary.main}>
                {stat.number}
              </Typography>
              <Typography variant="h6" color={theme.palette.text.secondary}>
                {stat.label}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Mission & Vision */}
      <Grid container spacing={6} sx={{ mb: 8 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%", background: theme.palette.background.paper }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h4" gutterBottom color={theme.palette.primary.main}>
                Our Mission
              </Typography>
              <Typography variant="body1" color={theme.palette.text.secondary} sx={{ lineHeight: 1.6 }}>
                To revolutionize the cleaning industry by providing reliable, tech-enabled 
                cleaning services that empower property managers and delight guests. We believe 
                every stay should begin with a spotless environment.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%", background: theme.palette.background.paper }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h4" gutterBottom color={theme.palette.primary.main}>
                Our Vision
              </Typography>
              <Typography variant="body1" color={theme.palette.text.secondary} sx={{ lineHeight: 1.6 }}>
                To become the most trusted cleaning partner for short-term rental hosts worldwide, 
                setting new standards for quality, reliability, and customer service in the 
                hospitality industry.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Values */}
      <Box sx={{ mb: 8 }}>
        <Typography variant="h3" textAlign="center" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
          Our Values
        </Typography>
        <Grid container spacing={4}>
          {values.map((value, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ textAlign: "center", height: "100%", background: theme.palette.background.paper }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h2" sx={{ mb: 2 }}>
                    {value.icon}
                  </Typography>
                  <Typography variant="h5" gutterBottom fontWeight="600">
                    {value.title}
                  </Typography>
                  <Typography variant="body2" color={theme.palette.text.secondary}>
                    {value.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* CTA Section */}
      <Box sx={{ textAlign: "center", background: alpha(theme.palette.primary.main, 0.05), p: 6, borderRadius: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
          Ready to Experience the FreshSweeper Difference?
        </Typography>
        <Typography variant="h6" color={theme.palette.text.secondary} sx={{ mb: 4 }}>
          Join thousands of satisfied hosts who trust us with their properties
        </Typography>
      </Box>
    </Container>
  );
};

export default About;